import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalResult } from '../../../../interfaces/modal-result.interface';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DEFAULT_ERROR_TEXT, PHONE_NUMBER_MASK } from '../../../../constants';
import { OtpRequestClientDto } from '../../../../dtos/otp-request.dto';
import { IconComponent } from '../../../../components/icon/icon.component';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerService } from '../../../../services/customer.service';
import { AuthService } from '../../../../services/auth.service';
import { NgxMaskPipe } from 'ngx-mask';
import { ResetPasswordComponent } from '../../../../components/reset-password/reset-password.component';
import { FormRadioInputComponent } from '../../../../components/form-radio-input/form-radio-input.component';


type ResetPasswordFormState = 'select-login' | 'otp';

@Component({
  selector: 'tc-reset-password-modal',
  templateUrl: './reset-password-modal.component.html',
  styleUrls: ['./reset-password-modal.component.scss'],
  imports: [
    TranslatePipe,
    IconComponent,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskPipe,
    ResetPasswordComponent,
    FormRadioInputComponent,
  ],
})
export class ResetPasswordModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<ResetPasswordModalComponent, ModalResult>);
  customerService = inject(CustomerService);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);

  state = signal<ResetPasswordFormState>('select-login');
  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  otpRequest = signal<OtpRequestClientDto>(null);
  private isFormErrorsVisible = signal<boolean>(false);

  hasBothEmailAndPhoneNumber = !!this.customerService.customer.contactInfo.email
    && !!this.customerService.customer.contactInfo.phoneNumber;
  loginControl: FormControl<string> = new FormControl('', Validators.required);

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    const emailOrPhoneNumber = this.customerService.customer.contactInfo.email
      || this.customerService.customer.contactInfo.phoneNumber;
    this.loginControl.setValue(emailOrPhoneNumber);
    this.loginControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetErrors());

    if (!this.hasBothEmailAndPhoneNumber && this.loginControl.valid) {
      this.state.set('otp');
    }
  }

  submit(): void {
    if (this.isLoading()) {
      return;
    }

    this.resetErrors();
    this.resetPasswordInit();
  }

  resetPasswordInit(): void {
    this.resetErrors();
    if (this.loginControl.invalid) {
      this.isFormErrorsVisible.set(true);
      return;
    }

    this.isLoading.set(true);
    this.authService.resetPasswordInit(this.loginControl.getRawValue())
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response.data);
          this.resetErrors();
          this.state.set('otp');
        },
        error: error => this.error.set(error.error?.message || DEFAULT_ERROR_TEXT),
      });
  }

  isControlInvalid(control: AbstractControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  resetErrors(): void {
    this.isFormErrorsVisible.set(false);
    this.error.set(null);
  }

  protected readonly PHONE_NUMBER_MASK = PHONE_NUMBER_MASK;
}
